import type { MediaItemInterface } from '@hypercodestudio/basler-components';

import { fetchContentfulAssetUrl } from '~/utils/contentful/fetchContentfulAssetUrl';
import { generateImageObject } from '~/utils/generateImageObject';
import {
  lightingAdvisorExampleImageOptions,
  lightingAdvisorSetupSketchOptions
} from '~/utils/imagePresetRegistry';
import type { LightingAdvisorConfiguration } from '~/utils/lightingAdvisor/configurationSchema';
import type {
  LightingAdvisorSetupField,
  LightingAnswers
} from '~/types/lightingAdvisor';

export type LightingAdvisorExampleObject = 'object1' | 'object2' | 'object3';
export type LightingAdvisorExampleStep = 1 | 2 | 3;

interface ExamplePair {
  before?: MediaItemInterface;
  after?: MediaItemInterface;
}

const COMPARISON_IMAGE_SIZE = { width: 908, height: 606 } as const;
const SETUP_SKETCH_SIZE = { width: 1380, height: 735 } as const;

export function useLightingAdvisorExampleImages(
  configuration: Ref<LightingAdvisorConfiguration>,
  step: Ref<LightingAdvisorExampleStep>,
  selectedObject: Ref<LightingAdvisorExampleObject>,
  answers: Ref<LightingAnswers>,
  focusedField: Ref<LightingAdvisorSetupField | null>
) {
  const { $activeLocale } = useNuxtApp();
  const pair = shallowRef<ExamplePair>({});
  const loading = ref(false);
  const urlCache = new Map<string, Promise<string | null>>();
  let refreshSequence = 0;

  /**
   * Step 2 shows a single setup sketch named after object movement, ambient
   * light and the focused measurement, mirroring the legacy asset naming.
   */
  function setupSketchAssetId(): string | undefined {
    const sketches = configuration.value.images?.examples?.step2;
    if (!sketches) {
      return undefined;
    }
    const base = [
      answers.value.a2s2q3 || 'no',
      answers.value.a2s2q4 || 'no'
    ].join('_');
    return (
      (focusedField.value
        ? sketches[`${base}_${focusedField.value}`]
        : undefined) ?? sketches[base]
    );
  }

  function configuredAssetIds(): [string | undefined, string | undefined] {
    const examples = configuration.value.images?.examples;

    if (step.value === 1) {
      const objectImages = examples?.step1?.[selectedObject.value];
      const selectedLightType = String(answers.value.a2s1q3 ?? '');
      const selectedImage =
        objectImages && selectedLightType in objectImages
          ? objectImages[
              selectedLightType as keyof NonNullable<typeof objectImages>
            ]
          : undefined;
      return [objectImages?.normal, selectedImage];
    }

    if (step.value === 2) {
      return [setupSketchAssetId(), undefined];
    }

    const spectrum = examples?.step3;
    const selectedFeature =
      answers.value.a2s3q1 === 'white'
        ? 'white'
        : String(answers.value.a2s3q2 ?? '');
    const beforeKey =
      selectedFeature === 'infrared' || selectedFeature === 'swir'
        ? `normal_${selectedFeature}`
        : 'normal';

    return [
      spectrum?.[beforeKey as keyof typeof spectrum],
      spectrum?.[selectedFeature as keyof typeof spectrum]
    ];
  }

  function resolveAsset(assetId: string): Promise<string | null> {
    const cacheKey = `${$activeLocale.value.contentfulCode}:${assetId}`;
    const cached = urlCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request = fetchContentfulAssetUrl(
      assetId,
      $activeLocale.value.contentfulCode
    );
    urlCache.set(cacheKey, request);
    return request;
  }

  function buildMedia(
    url: string | null,
    alt: string
  ): MediaItemInterface | undefined {
    if (!url) {
      return undefined;
    }
    const isSketch = step.value === 2;
    return generateImageObject(
      {
        url,
        ...(isSketch ? SETUP_SKETCH_SIZE : COMPARISON_IMAGE_SIZE),
        altTag: alt
      },
      isSketch
        ? lightingAdvisorSetupSketchOptions
        : lightingAdvisorExampleImageOptions
    );
  }

  async function refresh(): Promise<void> {
    const sequence = ++refreshSequence;
    const [beforeId, afterId] = configuredAssetIds();
    if (!beforeId) {
      pair.value = {};
      loading.value = false;
      return;
    }

    loading.value = true;
    const [beforeUrl, afterUrl] = await Promise.all([
      resolveAsset(beforeId),
      afterId ? resolveAsset(afterId) : Promise.resolve(null)
    ]);
    if (sequence !== refreshSequence) {
      return;
    }

    pair.value = {
      before: buildMedia(
        beforeUrl,
        step.value === 2
          ? 'Camera setup with the measured object'
          : 'Example object without selected lighting'
      ),
      after: buildMedia(afterUrl, 'Example object with selected lighting')
    };
    loading.value = false;
  }

  watch(
    () => [
      configuration.value.images?.examples,
      step.value,
      selectedObject.value,
      answers.value.a2s1q3,
      answers.value.a2s2q3,
      answers.value.a2s2q4,
      answers.value.a2s3q1,
      answers.value.a2s3q2,
      focusedField.value,
      $activeLocale.value.contentfulCode
    ],
    () => {
      void refresh();
    },
    { immediate: true, deep: true }
  );

  return {
    beforeImage: computed(() => pair.value.before),
    afterImage: computed(() => pair.value.after),
    loading
  };
}
